/* eslint-disable radix */
import React, { useState } from "react";
import { message, Alert, Row, Col } from "antd";
import moment from "moment";
import styled from "styled-components";
import Button from "common/button";

// hooks
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import useErrors from "hooks/useErrors";
import useLocale from "hooks/useLocale";

// graphql
import DISABLE_RENEW_SUBSCRIPTION from "graphql/mutations/DisableRenewSubscription";
import ENABLE_RENEW_SUBSCRIPTION from "graphql/mutations/EnableRenewSubscription";
import GET_PROGRAMS from "graphql/queries/GetPrograms";
import GET_MY_CREDITS from "graphql/queries/GetMyCredits";

// components
import ErrorAlerting from "common/error_alerting";
import { Subscription, Program } from "types";
import { H4 } from "common/text";
import {
  ProgramSelectorContainer,
  ProgramInfoContainer,
  ProgramDescription,
  Price,
  CurrencySign,
  PerUnitTime,
  SubscriptionStatusTag,
} from "common/program_selector";
import getCurrencySign from "utils/currency";
import screenSize from "utils/screenSize";
import outputManuallySetPrice from "utils/outputManuallySetPrice";
import resizeOverflowingPrice from "utils/resizeOverflowingPrice";
import { trackTrialUserCancelSubscription } from "utils/gtm";

import SubscribeAgainModal from "./SubscribeAgainModal";
import CancelSubscriptionModal from "../CancelSubscriptionModal";

// styles
import { ProgramOption } from "../styles";

const StyledSecondaryButton = styled(Button)`
  background-color: #d3e8ff;
  color: #1070b6;
  font-weight: bold;
  text-align: center;

  :hover,
  :active,
  :focus {
    background-color: #d3e8ff;
    filter: brightness(90%);
  }
`;

type TProps = {
  activeSubscription: Subscription;
  currency?: string;
  studentId: number;
};

const EditSubscription = ({
  activeSubscription,
  currency,
  studentId,
}: TProps) => {
  const { t } = useTranslation("my_credits");

  // hooks
  const [cancelSubModalVisble, setCancelSubModalVisble] = useState(false);
  const [subscribeAgainModalVisble, setSubscribeAgainModalVisble] =
    useState(false);
  const { errors, handleError, closeError } = useErrors();

  // queries
  const language = useLocale();

  const { data, loading } = useQuery(GET_PROGRAMS, {
    variables: {
      currency,
      language,
      studyLanguageId: null,
    },
  });

  // mutations
  const [disableRenew, { loading: loadingDisableRenew }] = useMutation(
    DISABLE_RENEW_SUBSCRIPTION,
    {
      refetchQueries: [{ query: GET_MY_CREDITS }],
    }
  );
  const [enableRenew, { loading: loadingEnableRenew }] = useMutation(
    ENABLE_RENEW_SUBSCRIPTION,
    {
      refetchQueries: [{ query: GET_MY_CREDITS }],
    }
  );

  if (loading) return <Alert type="info" message={t("Loading...")} />;

  const handleClick = async (action: string) => {
    try {
      switch (action) {
        case "disable-renew":
          await disableRenew();

          // Tracking trial users cancel subscription to GA
          if (activeSubscription?.status === "trialing") {
            trackTrialUserCancelSubscription();
          }

          message.info(t("Successfully cancelled!"));
          setCancelSubModalVisble(false);
          break;
        case "enable-renew":
          await enableRenew();
          message.info(t("Re-subscribed successfully!"));
          setSubscribeAgainModalVisble(false);
          break;
        default:
      }
    } catch (e) {
      handleError(e);
    }
  };

  const { renew, endDate } = activeSubscription;
  const renderCancelButton = (
    <div>
      <p style={{ color: "#282828" }}>
        {t("Your subscription will renew on")} {moment(endDate).format("LL")}.{" "}
        {t(
          " If you change your subscription above nothing will happen now. You will receive the updated credits and make payment only on"
        )}{" "}
        {moment(endDate).format("LL")}.
      </p>
      <StyledSecondaryButton
        disabled={loadingDisableRenew}
        onClick={() => setCancelSubModalVisble(true)}
      >
        {t("CANCEL SUBSCRIPTION")}
      </StyledSecondaryButton>
    </div>
  );

  const renderSubscribeButton = (
    <div>
      <p style={{ color: "#282828" }}>
        {moment(endDate).isAfter(moment())
          ? t("Your subscription will be expired on {{ date }}.", {
              date: moment(endDate).format("LL"),
            })
          : t("Your subscription was expired on {{ date }}.", {
              date: moment(endDate).format("LL"),
            })}
      </p>
      <Button
        disabled={loadingEnableRenew}
        onClick={() => setSubscribeAgainModalVisble(true)}
      >
        {t("SUBSCRIBE AGAIN")}
      </Button>
    </div>
  );

  const activeProgram: Program | undefined = data?.plans.find(
    (program: any) => program.id === activeSubscription.stripePriceId
  );

  const renderActiveSubscription = () => {
    // if there's no active subscription/program
    if (!activeProgram) {
      return (
        <div style={{ marginTop: 18 }}>
          <Alert
            type="warning"
            message={t(
              "You didn't subscribe to any program yet. Subscribe by selecting one above."
            )}
          />
        </div>
      );
    }

    const ActiveTag = (
      <SubscriptionStatusTag>{t("ACTIVE")}</SubscriptionStatusTag>
    );
    const PausedTag = (
      <SubscriptionStatusTag gray>{t("CANCELED")}</SubscriptionStatusTag>
    );

    const renderSubscriptionStatusTag = () => {
      if (activeSubscription?.renew) {
        return ActiveTag;
      }
      return PausedTag;
    };

    return (
      <ProgramSelectorContainer value={activeProgram.id}>
        <ProgramOption key={activeProgram.id} value={activeProgram.id} checked>
          <ProgramInfoContainer gutter={[10, 10]}>
            <ProgramDescription sm={16} xs={12}>
              <div style={{ whiteSpace: "normal" }}>{activeProgram.name}</div>
              {screenSize() === "mobile" && (
                <div style={{ flexBasis: "100%" }} />
              )}
              {renderSubscriptionStatusTag()}
            </ProgramDescription>
            <Price
              sm={8}
              xs={12}
              style={{
                fontSize: resizeOverflowingPrice(
                  outputManuallySetPrice(activeProgram.price)
                ),
              }}
            >
              <CurrencySign>
                <span // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: getCurrencySign(currency?.toString() || ""),
                  }}
                />
              </CurrencySign>
              {outputManuallySetPrice(activeProgram.price)}
              <PerUnitTime>{t("/month")}</PerUnitTime>
            </Price>
          </ProgramInfoContainer>
        </ProgramOption>
      </ProgramSelectorContainer>
    );
  };

  return (
    <div>
      <ErrorAlerting errors={errors} onClose={closeError} />

      <H4>{t("CURRENT SUBSCRIPTION")}</H4>
      <Row>
        <Col md={12} sm={24}>
          {renderActiveSubscription()}
        </Col>
      </Row>

      {renew ? renderCancelButton : renderSubscribeButton}
      <CancelSubscriptionModal
        visible={cancelSubModalVisble}
        onCancel={() => setCancelSubModalVisble(false)}
        onConfirm={() => handleClick("disable-renew")}
        loading={loadingDisableRenew}
        studentId={studentId}
      />
      <SubscribeAgainModal
        visible={subscribeAgainModalVisble}
        onCancel={() => setSubscribeAgainModalVisble(false)}
        onConfirm={() => handleClick("enable-renew")}
        loading={loadingEnableRenew}
      />
    </div>
  );
};

export default EditSubscription;
